import React, { useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import '../styles/startPositionSelector.css';

interface StartPositionSelectorProps {
    /** Current start position (0-23) */
    currentPosition: number;
    /** Callback when position is selected */
    onPositionChange: (position: number) => void;
}

/**
 * StartPositionSelector component - allows user to select starting cell position.
 *
 * Displays a 3×8 grid representing the label sheet where users can click
 * to select which cell should be the starting position for printing.
 * Useful when printing on partially used label sheets.
 */
export const StartPositionSelector: React.FC<StartPositionSelectorProps> = ({
    currentPosition,
    onPositionChange,
}) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [selectedPosition, setSelectedPosition] = useState(currentPosition);

    const handleOpen = () => {
        setSelectedPosition(currentPosition);
        setIsOpen(true);
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    const handleConfirm = () => {
        onPositionChange(selectedPosition);
        setIsOpen(false);
    };

    const handleReset = () => {
        setSelectedPosition(0);
    };

    const handleCellClick = (position: number) => {
        setSelectedPosition(position);
    };

    // Generate grid cells (3 columns × 8 rows = 24 cells)
    const cells = Array.from({ length: 24 }, (_, index) => {
        const row = Math.floor(index / 3) + 1;
        const col = (index % 3) + 1;
        const isSelected = index === selectedPosition;
        const isCurrent = index === currentPosition;

        return (
            <button
                key={index}
                className={`position-cell ${isSelected ? 'selected' : ''} ${isCurrent ? 'current' : ''}`}
                onClick={() => handleCellClick(index)}
                type="button"
            >
                <span className="cell-label">
                    {row},{col}
                </span>
                {isSelected && <span className="cell-checkmark">✓</span>}
            </button>
        );
    });

    return (
        <>
            <Button variant="outlined" onClick={handleOpen} size="small">
                {t('labelPrinting.changeStartPosition')}
            </Button>

            <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {t('labelPrinting.selectStartPosition')}
                </DialogTitle>
                <DialogContent>
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        gutterBottom
                    >
                        {t('labelPrinting.startPositionDescription')}
                    </Typography>
                    <Box mt={2} mb={2}>
                        <div className="position-grid">{cells}</div>
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                        {t('labelPrinting.currentPosition')}:{' '}
                        {t('labelPrinting.row')}{' '}
                        {Math.floor(selectedPosition / 3) + 1},{' '}
                        {t('labelPrinting.column')} {(selectedPosition % 3) + 1}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleReset} color="secondary">
                        {t('labelPrinting.resetToStart')}
                    </Button>
                    <Button onClick={handleClose}>{t('common.cancel')}</Button>
                    <Button
                        onClick={handleConfirm}
                        variant="contained"
                        color="primary"
                    >
                        {t('labelPrinting.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
